import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Star, MessageCircle } from "lucide-react";

const Stars = ({ rating }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        size={14}
        className={i <= rating ? "text-[#E9A23B] fill-[#E9A23B]" : "text-[#EBE8E2]"}
      />
    ))}
  </div>
);

export default function Feedback() {
  const [data, setData] = useState({ rows: [], average_rating: 0, count: 0 });

  useEffect(() => {
    api.get("/feedback").then((r) => setData(r.data || { rows: [], average_rating: 0, count: 0 }));
  }, []);

  const dist = [5, 4, 3, 2, 1].map((r) => ({
    rating: r,
    count: data.rows.filter((x) => x.rating === r).length,
  }));
  const maxBar = Math.max(1, ...dist.map((d) => d.count));

  return (
    <div className="p-6 sm:p-8" data-testid="feedback-page">
      <div className="mb-6">
        <h1 className="font-heading font-extrabold text-3xl text-[#2D2C2A]">Customer Feedback</h1>
        <p className="text-sm text-[#7A7873] mt-1">What guests are saying — captured via QR feedback link printed on bills.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-6 mb-8">
        <div className="bg-white border border-[#EBE8E2] rounded-lg p-6 text-center">
          <div className="text-xs uppercase tracking-wider text-[#7A7873]">Average Rating</div>
          <div className="font-heading font-extrabold text-5xl text-[#D95D39] my-2 font-mono-num" data-testid="avg-rating">
            {data.average_rating || "—"}
          </div>
          <Stars rating={Math.round(data.average_rating || 0)} />
          <div className="text-xs text-[#7A7873] mt-2">{data.count} reviews</div>
        </div>
        <div className="bg-white border border-[#EBE8E2] rounded-lg p-6">
          <div className="text-xs uppercase tracking-wider text-[#7A7873] mb-3">Distribution</div>
          {dist.map((d) => (
            <div key={d.rating} className="flex items-center gap-3 mb-1.5">
              <div className="w-6 text-xs font-mono-num">{d.rating}★</div>
              <div className="flex-1 bg-[#F4F2EE] h-3 rounded-full overflow-hidden">
                <div className="h-full bg-[#E9A23B]" style={{ width: `${(d.count / maxBar) * 100}%` }} />
              </div>
              <div className="w-8 text-right font-mono-num text-xs">{d.count}</div>
            </div>
          ))}
        </div>
      </div>

      <h2 className="font-heading font-bold text-xl text-[#2D2C2A] mb-3 flex items-center gap-2">
        <MessageCircle size={18} /> Recent Reviews
      </h2>
      <div className="space-y-3">
        {data.rows.map((r) => (
          <div key={r.id} className="bg-white border border-[#EBE8E2] rounded-lg p-4" data-testid={`feedback-${r.id}`}>
            <div className="flex items-start justify-between mb-1">
              <div>
                <Stars rating={r.rating} />
                <div className="text-sm font-semibold text-[#2D2C2A] mt-1">{r.name || "Anonymous"}</div>
                {r.phone && <div className="text-xs text-[#7A7873] font-mono-num">{r.phone}</div>}
              </div>
              <div className="text-xs text-[#7A7873]">{new Date(r.created_at).toLocaleString("en-IN")}</div>
            </div>
            {r.comments && <p className="text-sm text-[#2D2C2A] mt-2">{r.comments}</p>}
          </div>
        ))}
        {data.rows.length === 0 && (
          <div className="bg-white border border-[#EBE8E2] rounded-lg p-10 text-center text-[#7A7873]">
            No feedback yet. Print a feedback QR on bills and customers will leave reviews here.
          </div>
        )}
      </div>
    </div>
  );
}
